import { App, Typography } from 'antd';
import { type FC, useEffect, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import { formatError, logError } from '@/utils/formatError';
import { useUpdateWeekdayWindowProviders } from '../../query/calendarGrid.hooks';
import { ProviderSelect } from './ProviderSelect';

interface WeekdayProviderModalProps {
  open: boolean;
  close: () => void;
  weekdayWindowId: number | null;
  weekdayName: string;
  timeDuration: string;
  currentProviderIds: string[];
}

export const WeekdayProviderModal: FC<WeekdayProviderModalProps> = ({
  open,
  close,
  weekdayWindowId,
  weekdayName,
  timeDuration,
  currentProviderIds,
}) => {
  const { message } = App.useApp();
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  const { mutateAsync: updateProviders, isPending } = useUpdateWeekdayWindowProviders();

  useEffect(() => {
    if (open) {
      setSelectedProviders(currentProviderIds);
    }
  }, [open, currentProviderIds]);

  const handleClose = () => {
    setSelectedProviders([]);
    close();
  };

  const handleOk = async () => {
    if (!weekdayWindowId) return;

    try {
      await updateProviders({
        weekdayWindowId,
        providerIds: selectedProviders,
      });
      message.success('Providers updated successfully');
      handleClose();
    } catch (error) {
      const errorMessage = formatError(error, 'Update Providers');
      logError(error, 'Update Providers');
      message.error(`Failed to update providers: ${errorMessage}`);
    }
  };

  return (
    <AppModal
      title={`Edit Providers - ${weekdayName} (${timeDuration})`}
      open={open}
      onOk={handleOk}
      onCancel={handleClose}
      okButtonProps={{
        loading: isPending,
        disabled: selectedProviders.length === 0,
      }}
      cancelButtonProps={{
        disabled: isPending,
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong>
          Delivery Providers <span style={{ color: 'red' }}>*</span>
        </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <ProviderSelect
            value={selectedProviders}
            onChange={setSelectedProviders}
            disabled={isPending}
          />
        </div>
        {selectedProviders.length === 0 && (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            At least one provider is required
          </Typography.Text>
        )}
      </div>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        Note: This will update providers only for {weekdayName} at {timeDuration}. To update all
        days at once, edit the entire time slot.
      </Typography.Paragraph>
    </AppModal>
  );
};
